import { Box, Stack } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';

import { createAndSendExternalInvitation } from '@/domain/external-invitation/actions/create-and-send-external-invitation.action';
import { selectExternalInvitationsForScopes } from '@/domain/external-invitation/external-invitations.selectors';
import { getCategories } from '@/domain/external-invitation/utils/category/get-categories';
import { getScopeFromModeSectorAndCategory } from '@/domain/external-invitation/utils/scopes/get-scope';
import { getScopesFromModeAndSector } from '@/domain/external-invitation/utils/scopes/get-scopes';
import { ScoreType, Sector } from '@/domain/impact/impact.entity';
import { ProjectType } from '@/domain/pdf-reports/pdf-reports.type';
import { ProjectFestival } from '@/domain/project-festival/entities/project-festival.entity';
import {
  checkUserCreateFestival,
  selectCurrentProjectFestival,
} from '@/domain/project-festival/project-festival.selectors';
import { FSSecondaryButton } from '@/ui/components/button/FSSecondaryButton.tsx';
import {
  scrollQuizPanelToId,
  scrollQuizPanelToTop,
} from '@/ui/components/quiz-panel/QuizPanel.utils';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { DEFAULT_LAYOUT_MARGIN } from '@/ui/pages/app/components/layouts/DefaultLayout';
import { ModalReportExporter } from '@/ui/pages/app/components/project-page/ModalReportExporter';
import { PageProjectHeader } from '@/ui/pages/app/components/project-page/PageProjectHeader';
import {
  ProjectScores,
  ShowDetailsMode,
} from '@/ui/pages/app/components/project-page/ProjectScores';
import { ProjectScoresDetailsItemParams } from '@/ui/pages/app/components/project-page/ProjectScoresDetails/ProjectScoresDetails.types';
import { ImpactQuestionContext } from '@/ui/pages/app/components/quiz-panel/ImpactQuestionContext';
import { QuizPanel } from '@/ui/pages/app/components/quiz-panel/QuizPanel';
import { prepareQuizPanelDescription } from '@/ui/pages/app/components/quiz-panel/utils/prepare-quiz-panel-description';
import { InviteSubmitData } from '@/ui/pages/app/components/stakeholders/assign-external/components/FormExternalInvite';
import { ModalStakeholders } from '@/ui/pages/app/components/stakeholders/ModalStakeholders';
import { ProjectFestivalScoresDetails } from '@/ui/pages/app/project/festival/project/components/ProjectFestivalScoresDetails/ProjectFestivalScoresDetails';
import { ScoresBySector } from '@/ui/pages/app/project/festival/project/components/ScoresBySector/ScoresBySector';
import { ColorsRaw } from '@/ui/theme/colors-raw';

import { getFestivalCo2ExternalQuizFormForSector } from '../utils/festival-co2-external-quiz-form-data';
import { getFestivalCo2QuizFormForSector } from '../utils/festival-co2-quiz-form-data';
import { envFestivalQuizLayout } from '../utils/festival-env-quiz-layout';
import { essFestivalQuizLayout } from '../utils/festival-ess-quiz-layout';
import { prepareModalStakeholdersItems } from '../utils/prepare-modal-stakehodlers-iems';
import { ModalDuplicateProject } from './components/duplicate-project/ModalDuplicateProject';
import { HeaderButtons } from './components/HeaderButtons';
import { ModalImportAnswers } from './components/import-answers/ModalImportAnswers';

export function ProjectFestival() {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const project = useAppSelector(selectCurrentProjectFestival);

  const userCanCreateFestival = useAppSelector(checkUserCreateFestival);

  const handleBackButton = () => {
    navigate('/app/projects/festival');
  };

  const [isQuizLayoutDisplayed, setIsQuizLayoutDisplayed] = useState(false);
  const [selectedScoreType, setSelectedScoreType] = useState<ScoreType>('co2');
  const [selectedSector, setSelectedSector] = useState<Sector>('ARTISTS');
  const [selectedDetailsValueId, setSelectedDetailsValueId] =
    useState<string>('');
  const [panelOpen, setPanelOpen] = useState(false);
  const [hideCompleted, setHideCompleted] = useState(false);

  const onClickDetailsItem = (params: ProjectScoresDetailsItemParams) => {
    const { scoreType, id, sector, section } = params;
    setIsQuizLayoutDisplayed(true);
    setSelectedScoreType(scoreType); // this will change the scoretype in quiz-panel
    setSelectedDetailsValueId(id); // this will change the topics in quiz-panel (ess-env)
    if (scoreType === 'co2') {
      if (id) {
        scrollQuizPanelToId(`#${id.toLowerCase()}-form`); // for co2 all questions are in one view, so we scroll to category
      } else {
        scrollQuizPanelToTop();
      }
      if (sector) {
        setSelectedSector(sector);
      }
    }
    if (scoreType === 'ess' || scoreType === 'env') {
      if (section) {
        scrollQuizPanelToId(`#${section.toLowerCase()}-form`); // for ess-env we scroll to the section
      } else {
        scrollQuizPanelToTop();
      }
    }
  };

  const [stakeholdersModalOpen, setStakeholdersModalOpen] = useState(false);
  const [duplicateProjectModalOpen, setDuplicateProjectModalOpen] =
    useState(false);
  const [reportExporterModalOpen, setReportExporterModalOpen] = useState(false);
  const [importAnswersModalOpen, setImportAnswersModalOpen] = useState(false);

  const onClickStakeholders = () => {
    setStakeholdersModalOpen(true);
  };

  const onClickDuplicateProject = () => {
    if (userCanCreateFestival) {
      setDuplicateProjectModalOpen(true);
    } else {
      enqueueSnackbar(translate('License.MaxEvent'), {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        action: (
          <FSSecondaryButton onClick={() => navigate('/app/contact')}>
            {translate('License.MaxEventAction')}
          </FSSecondaryButton>
        ),
      });
    }
  };

  const onClickExportReport = () => {
    setReportExporterModalOpen(true);
  };

  const onClickImportAnswers = () => {
    setImportAnswersModalOpen(true);
  };

  const externalCategories = getCategories('festival', selectedSector);
  const externalScopes = getScopesFromModeAndSector('festival', selectedSector);
  const externalExistingInvitations = useAppSelector(
    selectExternalInvitationsForScopes(externalScopes),
  );

  const onSubmitInvite = async (data: InviteSubmitData) => {
    const scope = getScopeFromModeSectorAndCategory(
      'festival',
      data.sector,
      data.category,
    );
    const params = {
      name: data.name,
      email: data.email,
      limitDate: data.limitDate,
      eventPosition: undefined,
      title: '',
      projectId: project?.id || -1,
      sector: data.sector,
      scope: scope,
    };
    await dispatch(createAndSendExternalInvitation(params));
  };

  const existingInvitationsPerSector = {
    TEAM: useAppSelector(
      selectExternalInvitationsForScopes(
        getScopesFromModeAndSector('festival', 'TEAM'),
      ),
    ),
    ARTISTS: useAppSelector(
      selectExternalInvitationsForScopes(
        getScopesFromModeAndSector('festival', 'ARTISTS'),
      ),
    ),
    PUBLIC: useAppSelector(
      selectExternalInvitationsForScopes(
        getScopesFromModeAndSector('festival', 'PUBLIC'),
      ),
    ),
    SITE: useAppSelector(
      selectExternalInvitationsForScopes(
        getScopesFromModeAndSector('festival', 'SITE'),
      ),
    ),
  };
  const modalStakeholdersItems = prepareModalStakeholdersItems(
    existingInvitationsPerSector,
  );

  const quizPanelDescription = prepareQuizPanelDescription(
    selectedScoreType,
    selectedSector,
  );

  const enableExternalPublic = selectedSector === 'PUBLIC';

  if (!project) {
    return null;
  }

  return (
    <>
      <ModalStakeholders
        open={stakeholdersModalOpen}
        onClose={() => setStakeholdersModalOpen(false)}
        items={modalStakeholdersItems}
        onSubmitInvite={onSubmitInvite}
      />
      <ModalDuplicateProject
        open={duplicateProjectModalOpen}
        onClose={() => setDuplicateProjectModalOpen(false)}
        originalProject={project}
      />
      <ModalReportExporter
        open={reportExporterModalOpen}
        onClose={() => setReportExporterModalOpen(false)}
        project={project}
        projectType={ProjectType.FESTIVAL}
      />
      <ModalImportAnswers
        open={importAnswersModalOpen}
        onClose={() => setImportAnswersModalOpen(false)}
        project={project}
      />
      <Stack direction={'row'}>
        <Stack sx={{ minHeight: '100vh', width: 'calc(100% - 21rem)' }}>
          <Box sx={{ margin: DEFAULT_LAYOUT_MARGIN }}>
            <PageProjectHeader
              project={project}
              backButtonText={translate('ProjectsFestival.my-events')}
              onClickBack={handleBackButton}
              rightComponent={
                <HeaderButtons
                  project={project}
                  onClickStakeholders={onClickStakeholders}
                  onClickDuplicateProject={onClickDuplicateProject}
                  onClickExportReport={onClickExportReport}
                  onClickImportAnswers={onClickImportAnswers}
                />
              }
            />
            <MainContent
              project={project}
              onClickDetailsItem={onClickDetailsItem}
              setSelectedScoreType={setSelectedScoreType}
            />
          </Box>
        </Stack>
        {!isQuizLayoutDisplayed && <SidePanel project={project} />}
        {isQuizLayoutDisplayed && (
          <ImpactQuestionContext.Provider
            value={{
              panelOpen,
              hideCompleted,
              setHideCompleted,
              setPanelOpen,
            }}
          >
            <QuizPanel
              projectId={project.id}
              quizScoreType={selectedScoreType}
              quizSubType={selectedDetailsValueId}
              sector={selectedSector}
              externalCategories={externalCategories}
              externalExistingInvitations={externalExistingInvitations}
              onSubmitInvite={onSubmitInvite}
              co2FormData={getFestivalCo2QuizFormForSector(selectedSector)}
              co2ExternalFormData={getFestivalCo2ExternalQuizFormForSector(
                selectedSector,
              )}
              setIsQuizLayoutDisplayed={setIsQuizLayoutDisplayed}
              description={quizPanelDescription.description}
              descriptionTootltip={quizPanelDescription.descriptionTootltip}
              essQuizLayout={essFestivalQuizLayout}
              envQuizLayout={envFestivalQuizLayout}
              enableExternalPublic={enableExternalPublic}
              enableExternalAnswsersView={true}
              enableImportedAnswersView={true}
            />
          </ImpactQuestionContext.Provider>
        )}
      </Stack>
    </>
  );
}

function MainContent({
  project,
  onClickDetailsItem,
  setSelectedScoreType,
}: {
  project: ProjectFestival;
  onClickDetailsItem: (params: ProjectScoresDetailsItemParams) => void;
  setSelectedScoreType: React.Dispatch<React.SetStateAction<ScoreType>>;
}) {
  const [showDetailsMode, setShowDetailsMode] =
    useState<ShowDetailsMode>('none');

  const onClickScore = (type: ScoreType) => {
    if (showDetailsMode === type) {
      setShowDetailsMode('none');
    } else {
      setShowDetailsMode(type);
      setSelectedScoreType(type);
      scrollQuizPanelToTop();
    }
  };

  return (
    <Stack sx={{ mt: 3 }} spacing={3}>
      <ProjectScores
        project={project}
        showDetailsMode={showDetailsMode}
        onClickScore={onClickScore}
      />
      <ProjectFestivalScoresDetails
        project={project}
        showDetailsMode={showDetailsMode}
        onClickDetailsItem={onClickDetailsItem}
      />
    </Stack>
  );
}

function SidePanel({ project }: { project: ProjectFestival }) {
  return (
    <Box
      sx={{
        width: '21rem',
        borderLeft: `1px solid ${ColorsRaw.anthracite.Anthracite_4}`,
      }}
    >
      <ScoresBySector project={project} />
    </Box>
  );
}
